import cloneDeep from "lodash.clonedeep";
import set from "lodash.set";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { v4 as uuid } from "uuid";
import Button from "../components/Button";
import Form from "../components/Form";
import InputCalendar from "../components/InputCalendar";
import InputTextarea from "../components/InputTextarea";
import Modal from "../components/Modal";
import InputRadioGroup from "../components/schema/InputRadioGroup";
import { useContractQuery } from "../hooks";

const SubmitBespokeEndorsementModalForm = ({
  bespokeEndoFormValues,
  contractData,
  formData,
  handleClose,
  handleSubmitForm,
  isConfirmation,
  setBespokeEndoFormValues,
}) => {
  const inceptionIso = contractData?.boundQuote?.base?.inception_date;
  const effectiveFromIso = contractData?.submission?.quote?.effective_from;
  const expiryIso = contractData?.boundQuote?.base?.expiry_date;
  const effectiveFromDate = moment(effectiveFromIso || inceptionIso).toDate();
  const expiryDate = moment(expiryIso).toDate();

  const [formValues, setFormValues] = useState(bespokeEndoFormValues);

  const handleChange = (...args) => {
    const clonedFormValues = cloneDeep(formValues);

    if (args.length === 1) {
      const [event] = args;

      set(clonedFormValues, event.target.name, event.target.value);
    }

    if (args.length === 2) {
      const [value, name] = args;

      set(clonedFormValues, name, value);
    }

    setFormValues(clonedFormValues);
  };

  const handleSubmit = async () => {
    const prevBespokeEndos = contractData?.bespoke_endorsements || [];
    const newBespokeEndo = {
      content: formValues.bespoke_endorsement,
      id: uuid(),
      ref: (prevBespokeEndos.length + 1).toString().padStart(3, "0"),
    };

    handleClose();

    if (formValues.more_changes === "Yes" && !isConfirmation) {
      return setBespokeEndoFormValues(formValues);
    }

    return handleSubmitForm(
      {
        ...formData,
        quote: {
          ...contractData?.submission?.quote,
          ...formData.quote,
          is_bespoke: "Yes",
          effective_from: formValues.effective_from,
        },
      },
      {
        bespoke_endorsements: [...prevBespokeEndos, newBespokeEndo],
        is_bespoke: true,
      }
    );
  };

  return (
    <div className="p-6">
      <Form onSubmit={handleSubmit}>
        <InputCalendar
          className="mb-8"
          dayPickerProps={{ initialMonth: effectiveFromDate }}
          disableDaysAfter={expiryDate}
          disableDaysBefore={effectiveFromDate}
          isRequired
          labelText="It is requested this endorsement will be agreed with effect from:"
          name="effective_from"
          onChange={handleChange}
          value={formValues.effective_from}
        />

        <InputTextarea
          className="mb-8"
          isRequired
          labelText="Please enter your endorsement text. This will form part of the contractual endorsement."
          name="bespoke_endorsement"
          onChange={handleChange}
          value={formValues.bespoke_endorsement}
        />

        {!isConfirmation && (
          <InputRadioGroup
            className="mb-8"
            isRequired
            labelText="In addition to the above do you need to make any changes to the information supplied in the initial application?"
            name="more_changes"
            onChange={handleChange}
            options={["Yes", "No"]}
            value={formValues.more_changes || "No"}
          />
        )}

        <div className="flex">
          <Button
            className="mr-4"
            isDisabled={!formValues.effective_from}
            kind="primary"
            type="submit"
          >
            {formValues.more_changes === "Yes" ? "Continue" : "Submit"}
          </Button>

          <Button onClick={handleClose}>Cancel</Button>
        </div>
      </Form>
    </div>
  );
};

const SubmitBespokeEndorsementModal = ({
  bespokeEndoFormValues,
  formData,
  handleClose,
  handleSubmitForm,
  isConfirmation,
  setBespokeEndoFormValues,
}) => {
  const { contractData, isLoading } = useContractQuery();

  return (
    <Modal
      handleClose={handleClose}
      isLoading={isLoading}
      headingText={!isLoading && "Create your bespoke endorsement"}
    >
      <SubmitBespokeEndorsementModalForm
        bespokeEndoFormValues={bespokeEndoFormValues}
        contractData={contractData}
        formData={formData}
        handleClose={handleClose}
        handleSubmitForm={handleSubmitForm}
        isConfirmation={isConfirmation}
        setBespokeEndoFormValues={setBespokeEndoFormValues}
      />
    </Modal>
  );
};

SubmitBespokeEndorsementModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
};

export default SubmitBespokeEndorsementModal;
